import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState
} from 'react';

import UserDAO from '../storage/UserDAO';
import AuthDAO from '../storage/AuthDAO';
import { User } from '../models/User';
import CommonDAO from '../storage/CommonDAO';
import { login, logout} from '../service/AuthService';
import { getMe } from '../service/UserService';
import { Auth } from '../models/Auth';
import { getUserScopedStorageKeys } from '../storage/UserScopedStorageKeys';

type SignInProps = {
    email: string;
    password: string;
    scaCode?: string;
    rememberMe?: boolean;
}

type AuthContextProps = {
    user?: User;
    auth?: Auth;
    signIn: (data: SignInProps) => Promise<void>;
    signOut: (reason?: string) => Promise<void>;
    loading: boolean;
}

type AuthProviderProps = {
    children: ReactNode;
}

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [user, setUserState] = useState<User>();
    const [auth, setAuthState] = useState<Auth>();
    const [loading, setLoading] = useState(true);

    const signIn = async (data: SignInProps) => {

        AuthDAO.clear();
        UserDAO.clear();
        CommonDAO.clearLoggedInUserId();

        const { auth: authData, user } = await login(data.email, data.password, data.scaCode)
        const rememberMe = data?.rememberMe ?? false;
        CommonDAO.setLoggedInUserId(user.userId);
        AuthDAO.setAuth(authData);

        // Persist the Remember Me choice so loadUser knows what to do on next cold load
        localStorage.setItem(getUserScopedStorageKeys().REMEMBER_ME_KEY, String(rememberMe));

        // Always persist user — localStorage if Remember Me, sessionStorage if not
        UserDAO.setUser(user, rememberMe);

        setUserState(user);
        setAuthState(authData);
    };

    const signOut = async (reason?: string) => {

        try {
            logout();
            setUserState(undefined);
            localStorage.removeItem(getUserScopedStorageKeys().REMEMBER_ME_KEY);
            AuthDAO.clear();
            UserDAO.clear();
            CommonDAO.clearLoggedInUserId();
        } catch (error) {
            console.error('Error during sign out:', error);
        }
    };

    useEffect(() => {
        const loadUser = async () => {
            try {
                const authData = AuthDAO.getAuth();

                if (!authData) {
                    // No tokens at all — user must log in
                    return;
                }

                // Phase 1: user in localStorage (rememberMe) or sessionStorage (same tab after F5)
                const storedUser = UserDAO.getUser();
                if (storedUser) {
                    setUserState(storedUser);
                    setAuthState(authData);
                    return;
                }

                // Phase 2: tokens exist but no user in storage
                // Only restore from server if the user chose Remember Me.
                // Without Remember Me the session ends when the browser/tab closes.
                const rememberMeFlag = localStorage.getItem(getUserScopedStorageKeys().REMEMBER_ME_KEY);
                if (rememberMeFlag !== 'true') {
                    // User did not choose Remember Me — tokens should not outlive the session
                    AuthDAO.clear();
                    localStorage.removeItem(getUserScopedStorageKeys().REMEMBER_ME_KEY);
                    CommonDAO.clearLoggedInUserId();
                    return;
                }

                try {
                    const freshUser = await getMe();
                    setUserState(freshUser);
                    setAuthState(authData);
                    // Cache in localStorage (rememberMe=true path) so next load is instant
                    UserDAO.setUser(freshUser, true);
                } catch {
                    // Tokens are invalid or expired — clear everything so user is sent to login
                    AuthDAO.clear();
                    UserDAO.clear();
                    localStorage.removeItem(getUserScopedStorageKeys().REMEMBER_ME_KEY);
                    CommonDAO.clearLoggedInUserId();
                }
            } catch (error) {
                console.error('Error loading user:', error);
            } finally {
                setLoading(false);
            }
        };
        loadUser();
    }, []);

    return (
        <AuthContext.Provider value={{ user, signIn, signOut, auth, loading }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuthContext = (): AuthContextProps => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};
